import { Field, useFormikContext } from "formik"
import { Eye, EyeOff } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import Error from "@/components/Error/Error"
import RequiredInd from "@/components/RequiredInd/RequiredInd"

import styles from "./Inputs.module.css"

interface TextInputProps {
  id: string
  label: string
  placeholder: string
  type?: string
  name: string
  error?: React.ReactNode
  backError?: string
  className?: string
  row?: boolean
  disabled?: boolean
  required?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  readOnly?: boolean
  value?: string
  as?: string
  clear?: boolean
}

export default function TextInput({
  label,
  placeholder,
  id,
  type = "text",
  name,
  error,
  backError,
  className,
  row = false,
  disabled = false,
  required = false,
  onChange,
  onBlur,
  readOnly = false,
  value,
  as,
  clear = false,
}: TextInputProps) {
  const formikContext = useFormikContext()
  const [showPassword, setShowPassword] = useState(false)

  const { i18n } = useTranslation()

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev)
  }

  const inputType = type === "password" && showPassword ? "text" : type

  const { t } = useTranslation()

  return (
    <div
      className={`inp-group flex-column text-inp d-flex ${
        row ? "flex-md-row align-items-md-center gap-3" : "flex-column gap-2"
      }  w-100 ${className}`}
    >
      {label && (
        <div className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            <label className="modal__label m-0 fw-bold" htmlFor={id}>
              {label} {row ? ":" : ""}
            </label>
            {required ? <RequiredInd /> : ""}
          </div>
          {clear && (
            <button
              type="button"
              className={styles["btn-clear"]}
              onClick={() => formikContext?.setFieldValue(name, "")}
            >
              {t("buttons.clear")}
            </button>
          )}
        </div>
      )}
      <div className="w-100 position-relative">
        {as == "field" ? (
          <TextField
            type={inputType}
            placeholder={placeholder}
            name={name}
            id={id}
            disabled={disabled}
            onChange={onChange}
            readOnly={readOnly}
          />
        ) : (
          <TextInp
            type={inputType}
            placeholder={placeholder}
            name={name}
            id={id}
            disabled={disabled}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            readOnly={readOnly}
          />
        )}

        {/* Eye Icon for Password Visibility Toggle */}
        {type === "password" && (
          <span
            style={{
              position: "absolute",
              ...(i18n.language === "ar"
                ? { left: "20px" }
                : { right: "20px" }),
              // right:   "20px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              zIndex: "10",
            }}
            // className={styles["eye-icon"]}
            onClick={handleTogglePassword}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </span>
        )}

        {/* Error Message */}
      </div>
      <Error>{backError || error}</Error>
    </div>
  )
}

interface TextFieldProps {
  type: string
  placeholder: string
  name: string
  id: string
  disabled: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  readOnly: boolean
}

function TextField({
  type,
  placeholder,
  name,
  id,
  disabled,
  onChange,
  readOnly,
}: TextFieldProps) {
  let otherProps = {}
  if (onChange) otherProps = { onChange }
  return (
    <Field
      type={type}
      name={name}
      placeholder={placeholder}
      id={id}
      className={`${styles.input} w-100`}
      disabled={disabled}
      readOnly={readOnly}
      {...otherProps}
    />
  )
}

interface TextInpProps {
  type: string
  placeholder: string
  name: string
  id: string
  disabled: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  value?: string
  readOnly: boolean
}

function TextInp({
  type,
  placeholder,
  name,
  id,
  disabled,
  onChange,
  onBlur,
  value,
  readOnly,
}: TextInpProps) {
  return (
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      id={id}
      className={`${styles.input} w-100`}
      disabled={disabled}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      readOnly={readOnly}
    />
  )
}
